import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

/**
 * BulkConfirmDialog — confirmation modal that fronts every bulk action.
 *
 * Props:
 *   open, onOpenChange
 *   title, description       — headline + supporting copy
 *   itemsSummary             — small serif list of the selected items (already truncated by caller)
 *   confirmLabel             — CTA text (default "Confirm")
 *   confirmTone              — "primary" (ink) | "danger" (scarlet). Default "primary".
 *   requireNote              — if true, renders a textarea; onConfirm receives the note.
 *   notePlaceholder          — placeholder for that textarea
 *   noteMinLength            — min chars (default 8) when requireNote is true
 *   onConfirm(note?)         — async fn; dialog stays open until it resolves, then closes.
 */
export const BulkConfirmDialog = ({
  open, onOpenChange, title, description, itemsSummary,
  confirmLabel = "Confirm", confirmTone = "primary",
  requireNote = false, notePlaceholder = "", noteMinLength = 8,
  onConfirm,
}) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setNote("");
      setBusy(false);
      setError("");
    }
  }, [open]);

  const disabled = busy || (requireNote && note.trim().length < noteMinLength);
  const toneCls = confirmTone === "danger"
    ? "bg-scarlet text-sand hover:brightness-110"
    : "bg-ink text-sand hover:bg-forest";

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      await onConfirm(requireNote ? note.trim() : undefined);
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "That didn't go through. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-confirm-dialog" className="max-w-md border-border bg-sand p-0 gap-0 sm:rounded-lg">
        <div className="relative overflow-hidden bg-beige px-8 py-7">
          <span className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-yellow/40 blur-3xl" />
          <span className="pointer-events-none absolute -left-8 -bottom-10 h-32 w-32 rounded-full bg-tangerine/25 blur-3xl" />
          <DialogTitle className="relative font-serif text-3xl font-normal leading-[1.05] text-ink">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="relative mt-2 text-sm leading-relaxed text-olive">
              {description}
            </DialogDescription>
          )}
        </div>

        <div className="space-y-4 px-8 py-6">
          {itemsSummary && (
            <div data-testid="bulk-confirm-items" className="max-h-52 overflow-y-auto rounded-md border border-border bg-bone p-4">
              {itemsSummary}
            </div>
          )}

          {requireNote && (
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
                Note to the student
              </span>
              <textarea
                data-testid="bulk-confirm-note"
                rows={4}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={notePlaceholder}
                className="mt-2 w-full rounded-md border border-border bg-bone px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
              />
              <span className="mt-1 block text-[11px] text-olive">
                Minimum {noteMinLength} characters. This message will be shared with the student.
              </span>
            </label>
          )}

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              data-testid="bulk-confirm-error"
              className="flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} /> {error}
            </motion.p>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              data-testid="bulk-confirm-cancel"
              className="rounded-full border border-border bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={run}
              disabled={disabled}
              data-testid="bulk-confirm-submit"
              className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${toneCls}`}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
